import { Box, Collapse, Divider, Flex, Text, Tooltip } from '@chakra-ui/react';
import {
  Button,
  ButtonIcon,
  ButtonText,
  Checkbox,
  Modal,
  Switch,
  Table
} from '@morressier/alembic';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import dateFormat from 'dateformat';
import { useState } from 'react';
import { IDomainCommandType } from '@publishing-engine/domain/actions/v1/commands/index.js';
import { fetchSubmittedReviewers } from 'src/api/queries';
import { getAllTemplatesByCommand } from 'src/api/email-service';
import RecommendationBadge from 'src/components/RecommendationBadge';
import { DividerVertical } from '../OverviewTab';
import PreviewEmail from 'src/components/EmailModule/PreviewEmail';

interface NotifyReviewersToggleProps {
  submissionId: string;
  organizationId: string;
  commandName: IDomainCommandType;
  isEnabled: boolean;
  selectedReviewerIds: string[];
  onChange: (enabled: boolean, reviewerIds: string[]) => void;
}

const NotifyReviewersToggle = ({
  submissionId,
  organizationId,
  commandName,
  isEnabled,
  selectedReviewerIds,
  onChange
}: NotifyReviewersToggleProps) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const { data: reviewers = [] } = useQuery({
    queryKey: ['submitted-reviewers', submissionId],
    queryFn: () => fetchSubmittedReviewers(submissionId)
  });
  const allReviewerIds = reviewers.map(reviewer => reviewer.reviewerId);
  const {
    data: templateResponse,
    refetch: fetchTemplates,
    isFetching: isFetchingTemplates
  } = useQuery({
    queryKey: ['reviewer-notification-template', organizationId],
    queryFn: () =>
      getAllTemplatesByCommand({
        organizationId,
        commandName,
        query: { customizable: false }
      }),
    enabled: false
  });

  const handlePreviewTemplate = async () => {
    const { data } = await fetchTemplates();
    if (data?.data.length) {
      setIsPreviewOpen(true);
    }
  };
  const previewTemplate =
    templateResponse?.data.find(template => template.default) ??
    templateResponse?.data[0];

  return (
    <Box
      marginTop={4}
      border="1px solid"
      borderColor="neutralPrimaryBorder"
      borderRadius="md"
    >
      <Flex
        paddingX={4}
        paddingY={2}
        alignItems="center"
        justifyContent="space-between"
        gap={3}
      >
        <Box flex={1} minWidth={0}>
          <Flex alignItems="center" gap={2}>
            <Text textStyle="headingItem">
              Notify Reviewers via Email ({selectedReviewerIds.length}/
              {reviewers.length})
            </Text>
            <Tooltip
              label="Choose the reviewers you want to notify about the decision for this submission. Only reviewers who have completed their review for this submission will be listed here."
              hasArrow
              placement="top"
            >
              <Box>
                <ButtonIcon
                  size="small"
                  ariaLabel="Send a separate email to reviewers informing them of this decision."
                  icon={<FontAwesomeIcon icon={faInfoCircle} />}
                />
              </Box>
            </Tooltip>
          </Flex>
          <Text textStyle="textContext">
            Send a separate email to reviewers informing them of this decision.
          </Text>
        </Box>
        <Flex alignItems="center" gap={3} flexShrink={0}>
          <Button
            variant="secondary"
            disabled={!isEnabled || !reviewers.length}
            isLoading={isFetchingTemplates}
            onClick={handlePreviewTemplate}
          >
            Preview Template
          </Button>
          <DividerVertical />
          <Switch
            isChecked={isEnabled}
            isDisabled={!reviewers.length}
            aria-label="Notify reviewers of the decision"
            onChange={event =>
              onChange(
                event.target.checked,
                event.target.checked ? allReviewerIds : []
              )
            }
          />
        </Flex>
      </Flex>
      <Collapse in={isEnabled && reviewers.length > 0} animateOpacity>
        <Divider />
        <Box paddingX={4} paddingY={3}>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            marginBottom={2}
          >
            <Text textStyle="textContext">Select reviewers to notify</Text>
            <Flex alignItems="center" gap={2}>
              <ButtonText
                variant="primary"
                onClick={() => onChange(true, allReviewerIds)}
                disabled={selectedReviewerIds.length === allReviewerIds.length}
              >
                Select All
              </ButtonText>
              <ButtonText variant="caution" onClick={() => onChange(true, [])}>
                Clear
              </ButtonText>
            </Flex>
          </Flex>
          <Box></Box>
          <Box border="1px solid" borderColor="neutralPrimaryBorder">
            <Table>
              <Table.HeaderRow
                backgroundColor="surfaceSecondaryFill"
                paddingX={4}
                paddingY={2}
              >
                <Table.HeaderCell flex={2} textAlign="left">
                  Reviewer
                </Table.HeaderCell>
                <Table.HeaderCell flex={2} textAlign="left">
                  <Flex alignItems="center" gap={1}>
                    Last Recommendation
                    <Tooltip
                      label="Only the reviewer's most recent recommendation from the last round of submission will be shown here."
                      hasArrow
                      placement="top"
                    >
                      <Box>
                        <ButtonIcon
                          size="small"
                          ariaLabel="Send a separate email to reviewers informing them of this decision."
                          icon={<FontAwesomeIcon icon={faInfoCircle} />}
                        />
                      </Box>
                    </Tooltip>
                  </Flex>
                </Table.HeaderCell>
                <Table.HeaderCell flex={1} textAlign="left">
                  Reviewed on
                </Table.HeaderCell>
              </Table.HeaderRow>
              <Table.Body>
                {reviewers.map(reviewer => {
                  const isChecked = selectedReviewerIds.includes(
                    reviewer.reviewerId
                  );
                  return (
                    <Table.Row key={reviewer.reviewerId} padding={4}>
                      <Table.RowCell flex={2}>
                        <Checkbox
                          id={`notify-reviewer-${reviewer.reviewerId}`}
                          name={`notify-reviewer-${reviewer.reviewerId}`}
                          ariaLabel={`Select reviewer ${reviewer.reviewerName}`}
                          isChecked={isChecked}
                          onChange={event =>
                            onChange(
                              true,
                              event.target.checked
                                ? [...selectedReviewerIds, reviewer.reviewerId]
                                : selectedReviewerIds.filter(
                                    id => id !== reviewer.reviewerId
                                  )
                            )
                          }
                        >
                          <Text textStyle="textBody">
                            {reviewer.reviewerName}
                          </Text>
                        </Checkbox>
                      </Table.RowCell>
                      <Table.RowCell flex={2}>
                        <RecommendationBadge
                          recommendation={reviewer.recommendation}
                        />
                      </Table.RowCell>
                      <Table.RowCell flex={1}>
                        <Text textStyle="textContext" color="textSecondary">
                          {reviewer.reviewedOn
                            ? dateFormat(reviewer.reviewedOn, 'mmm d, yyyy')
                            : '-'}
                        </Text>
                      </Table.RowCell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </Box>
        </Box>
      </Collapse>
      {isPreviewOpen && previewTemplate && (
        <Modal isOpen onClose={() => setIsPreviewOpen(false)} width="800px">
          <Flex
            flexDirection="column"
            gap={4}
            padding={6}
            maxHeight="80vh"
            overflowY="auto"
          >
            <Text textStyle="headingGroup">Preview Template</Text>
            <PreviewEmail template={previewTemplate} />
          </Flex>
        </Modal>
      )}
    </Box>
  );
};

export default NotifyReviewersToggle;
